'use client';

import { useEffect, useRef, useState } from 'react';
import ParticleCanvas from '../home/components/ParticleCanvas';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PrivacyPage() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => {
      if (sectionRef?.current) {
        observer?.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <>
      <ParticleCanvas />
      <div className="vignette" aria-hidden="true" />
      <Header />
      <main className="relative min-h-screen pt-32 pb-20 px-6" style={{ zIndex: 2 }}>
        <div 
          ref={sectionRef}
          className={`max-w-[800px] mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Hero Title */}
          <h1 
            className="text-5xl md:text-6xl font-black text-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #c4beff 50%, #6a5cff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Privacy Policy
          </h1>

          {/* Subtitle */}
          <p className="text-center text-vanta-muted text-lg mb-16">
            Learn how VantaRust collects and protects your data.
          </p>

          {/* Content Card */}
          <div 
            className="rounded-3xl p-8 md:p-12"
            style={{
              background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.08) 0%, rgba(139, 127, 255, 0.05) 100%)',
              border: '2px solid rgba(106, 92, 255, 0.2)',
              backdropFilter: 'blur(30px)',
              boxShadow: '0 25px 80px rgba(106, 92, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="space-y-8 text-vanta-text">
              {/* Last Updated */}
              <p className="text-sm text-vanta-muted italic">Last updated: February 22, 2026</p>

              {/* Introduction */}
              <section>
                <p className="leading-relaxed">
                  This Privacy Policy explains how VantaRust ("we", "us", or "our") collects, uses, stores, and protects your personal data when you use vantarust.com, our Rust game servers, and related services (collectively, the "Services").
                </p>
                <p className="leading-relaxed mt-4">
                  By using our Services, you agree to the collection and use of information in accordance with this Privacy Policy.
                </p>
              </section>

              {/* 1. Data Controller */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">1. Data Controller</h2>
                <p className="leading-relaxed mb-4">
                  The data controller responsible for processing your personal data is:
                </p>
                <div className="space-y-2">
                  <p className="font-semibold">VantaRust</p>
                  <p>Switzerland</p>
                  <p>Email: <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors">support@vantarust.com</a></p>
                  <p>Website: <a href="https://vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors">https://vantarust.com</a></p>
                </div>
              </section>

              {/* 2. Personal Data We Collect */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">2. Personal Data We Collect</h2>
                <p className="leading-relaxed mb-4">
                  We collect personal data in the following ways:
                </p>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">2.1 Website Access Data</h3>
                    <p className="leading-relaxed mb-2">When you visit our website, we may automatically collect:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>IP address</li>
                      <li>Browser type and version</li>
                      <li>Device type and operating system</li>
                      <li>Referrer URL</li>
                      <li>Pages visited</li>
                      <li>Date and time of access</li>
                    </ul>
                    <p className="leading-relaxed mt-2">This data is necessary to ensure website functionality and security.</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">2.2 Game Server Data</h3>
                    <p className="leading-relaxed mb-2">When you connect to our Rust servers, we may collect:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Steam ID</li>
                      <li>Steam profile name</li>
                      <li>IP address</li>
                      <li>Connection logs</li>
                      <li>Gameplay information</li>
                      <li>Server statistics</li>
                      <li>Chat messages</li>
                    </ul>
                    <p className="leading-relaxed mt-2">This data is necessary to operate the servers, enforce rules, and prevent abuse.</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">2.3 Support and Communication Data</h3>
                    <p className="leading-relaxed mb-2">If you contact us, we may collect:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Email address</li>
                      <li>Discord username and ID</li>
                      <li>Message content</li>
                      <li>Support history</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 3. Backend and Data Storage */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. Backend and Data Storage (Supabase)</h2>
                <p className="leading-relaxed mb-4">
                  We use Supabase Inc. as our backend and database provider.
                </p>
                <div className="space-y-2 mb-4">
                  <p className="font-semibold">Provider:</p>
                  <p>Supabase Inc.</p>
                  <p>970 Towner Lane</p>
                  <p>Los Altos, CA 94024</p>
                  <p>United States</p>
                  <p><a href="https://supabase.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors">https://supabase.com</a></p>
                </div>
                <p className="leading-relaxed mb-2">
                  Supabase may process and store personal data on our behalf, including:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                  <li>Account identifiers</li>
                  <li>Steam ID</li>
                  <li>IP address</li>
                  <li>Gameplay data</li>
                  <li>Support data</li>
                </ul>
                <p className="leading-relaxed mb-2">
                  Supabase acts as a data processor under a data processing agreement.
                </p>
                <p className="leading-relaxed mb-2">
                  Data may be stored on servers outside Switzerland or the European Economic Area.
                </p>
                <p className="leading-relaxed">
                  Appropriate safeguards are applied in accordance with applicable data protection laws.
                </p>
              </section>

              {/* 4. Purpose of Processing */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. Purpose of Processing</h2>
                <p className="leading-relaxed mb-2">
                  We process personal data for the following purposes:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Providing and operating our Services</li>
                  <li>Maintaining server functionality</li>
                  <li>Enforcing rules and preventing cheating</li>
                  <li>Providing user support</li>
                  <li>Ensuring security</li>
                  <li>Preventing abuse and fraud</li>
                  <li>Improving our Services</li>
                </ul>
              </section>

              {/* 5. Legal Basis */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Legal Basis</h2>
                <p className="leading-relaxed mb-2">
                  We process personal data based on:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Legitimate interest in operating and securing our Services</li>
                  <li>Performance of a contract when providing Services</li>
                  <li>Legal obligations where applicable</li>
                  <li>User consent where required</li>
                </ul>
              </section>

              {/* 6. Data Sharing */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Data Sharing</h2>
                <p className="leading-relaxed mb-4">
                  We do not sell personal data.
                </p>
                <p className="leading-relaxed mb-2">
                  We may share personal data with:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Supabase Inc. (backend provider)</li>
                  <li>Hosting providers</li>
                  <li>Infrastructure and security providers</li>
                  <li>Steam and Facepunch Studios (game integration)</li>
                  <li>Discord (if you use our Discord services)</li>
                  <li>Authorities if required by law</li>
                </ul>
              </section>

              {/* 7. Data Retention */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Data Retention</h2>
                <p className="leading-relaxed mb-4">
                  We retain personal data only as long as necessary for the purposes described in this policy.
                </p>
                <p className="leading-relaxed mb-2">
                  Retention periods may vary depending on:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                  <li>Legal requirements</li>
                  <li>Security needs</li>
                  <li>Service functionality</li>
                </ul>
                <p className="leading-relaxed mb-2">
                  We may retain data longer if required to:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Resolve disputes</li>
                  <li>Enforce agreements</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              {/* 8. Data Security */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Data Security</h2>
                <p className="leading-relaxed mb-2">
                  We implement appropriate technical and organizational measures to protect personal data against:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                  <li>Unauthorized access</li>
                  <li>Loss</li>
                  <li>Misuse</li>
                  <li>Alteration</li>
                </ul>
                <p className="leading-relaxed">
                  However, no method of transmission over the Internet is completely secure.
                </p>
              </section>

              {/* 9. International Data Transfers */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. International Data Transfers</h2>
                <p className="leading-relaxed mb-4">
                  Your data may be transferred to and processed in countries outside Switzerland or the European Economic Area.
                </p>
                <p className="leading-relaxed mb-2">
                  Where required, we use appropriate safeguards such as:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Data processing agreements</li>
                  <li>Standard contractual clauses</li>
                </ul>
              </section>

              {/* 10. Your Rights */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">10. Your Rights</h2>
                <p className="leading-relaxed mb-2">
                  Under applicable data protection laws, you may have the right to:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                  <li>Request access to your personal data</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Request restriction of processing</li>
                  <li>Object to processing</li>
                  <li>Request data portability</li>
                </ul>
                <p className="leading-relaxed mb-2">
                  To exercise your rights, contact:
                </p>
                <p className="mb-2">
                  <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors font-semibold">
                    support@vantarust.com
                  </a>
                </p>
                <p className="leading-relaxed">
                  We may require identity verification.
                </p>
              </section>

              {/* 11. Children's Privacy */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">11. Children's Privacy</h2>
                <p className="leading-relaxed mb-2">
                  Our Services are not intended for children under 13.
                </p>
                <p className="leading-relaxed mb-2">
                  We do not knowingly collect personal data from children.
                </p>
                <p className="leading-relaxed">
                  If you believe a child has provided personal data, contact us.
                </p>
              </section>

              {/* 12. Third-Party Services */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">12. Third-Party Services</h2>
                <p className="leading-relaxed mb-2">
                  Our Services may integrate with third-party providers including:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                  <li>Steam (Valve Corporation)</li>
                  <li>Facepunch Studios Ltd</li>
                  <li>Discord Inc.</li>
                  <li>Supabase Inc.</li>
                </ul>
                <p className="leading-relaxed">
                  These services have their own privacy policies.
                </p>
              </section>

              {/* 13. Changes to This Privacy Policy */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">13. Changes to This Privacy Policy</h2>
                <p className="leading-relaxed mb-2">
                  We may update this Privacy Policy at any time.
                </p>
                <p className="leading-relaxed mb-2">
                  The latest version will always be published on this page.
                </p>
                <p className="leading-relaxed">
                  Continued use of the Services constitutes acceptance.
                </p>
              </section>

              {/* 14. Contact */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">14. Contact</h2>
                <p className="leading-relaxed mb-2">
                  For privacy-related inquiries:
                </p>
                <p className="mb-2">
                  Email: <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors font-semibold">support@vantarust.com</a>
                </p>
                <p>
                  Website: <a href="https://vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors font-semibold">https://vantarust.com</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}